/**
 * NER-4488 fase 3 — Cartas iniciales por rubro.
 *
 * Cada rubro define categorías (con área de preparación) e items de ejemplo
 * con precio de referencia. Al aplicarla, el admin crea categorías + items
 * (marcados NO disponibles para que el dueño revise precios antes de publicar)
 * y los grupos de modificadores típicos del rubro (plantillas de fase 2).
 *
 * Textos en i18n: `starterMenus.{rubro}.{name,description}`,
 * `starterMenus.categories.{key}` y `starterMenus.items.{key}`.
 * Módulo puro: sin React ni services/api.
 */

/**
 * @typedef {Object} StarterCategory
 * @property {string} key                 i18n `starterMenus.categories.{key}`
 * @property {'kitchen'|'bar'|'both'} preparationArea
 * @property {Array<{key: string, price: number}>} items  i18n `starterMenus.items.{key}` + precio ARS de referencia
 */

/**
 * @typedef {Object} StarterMenu
 * @property {string} id
 * @property {string} icon               nombre de icono lucide-react
 * @property {StarterCategory[]} categories
 * @property {string[]} modifierTemplateIds  ids de `src/data/modifierTemplates.js`
 */

const DRINKS_BASIC = [
  { key: 'soda', price: 1800 },
  { key: 'water', price: 1200 },
  { key: 'beer', price: 3200 },
  { key: 'lemonade', price: 2200 },
];

/** @type {StarterMenu[]} */
export const STARTER_MENUS = [
  {
    id: 'burger',
    icon: 'Sandwich',
    modifierTemplateIds: ['extras', 'without', 'bread'],
    categories: [
      {
        key: 'burgers',
        preparationArea: 'kitchen',
        items: [
          { key: 'classicBurger', price: 7500 },
          { key: 'cheeseBurger', price: 8200 },
          { key: 'doubleBurger', price: 9800 },
          { key: 'veggieBurger', price: 7900 },
        ],
      },
      {
        key: 'sides',
        preparationArea: 'kitchen',
        items: [
          { key: 'fries', price: 3200 },
          { key: 'cheeseFries', price: 4200 },
          { key: 'onionRings', price: 3800 },
        ],
      },
      { key: 'drinks', preparationArea: 'bar', items: DRINKS_BASIC },
      {
        key: 'desserts',
        preparationArea: 'kitchen',
        items: [
          { key: 'brownie', price: 3500 },
          { key: 'iceCream', price: 3000 },
        ],
      },
    ],
  },
  {
    id: 'pizza',
    icon: 'Pizza',
    modifierTemplateIds: ['size'],
    categories: [
      {
        key: 'pizzas',
        preparationArea: 'kitchen',
        items: [
          { key: 'muzzarella', price: 9500 },
          { key: 'napolitana', price: 11000 },
          { key: 'fugazzeta', price: 11500 },
          { key: 'calabresa', price: 12000 },
          { key: 'especial', price: 12500 },
        ],
      },
      {
        key: 'empanadas',
        preparationArea: 'kitchen',
        items: [
          { key: 'meatEmpanada', price: 1400 },
          { key: 'hamCheeseEmpanada', price: 1400 },
          { key: 'humitaEmpanada', price: 1400 },
        ],
      },
      {
        key: 'drinks',
        preparationArea: 'bar',
        items: [...DRINKS_BASIC, { key: 'wine', price: 6500 }],
      },
      {
        key: 'desserts',
        preparationArea: 'kitchen',
        items: [
          { key: 'flan', price: 3200 },
          { key: 'tiramisu', price: 3800 },
        ],
      },
    ],
  },
  {
    id: 'cafe',
    icon: 'Coffee',
    modifierTemplateIds: ['milk', 'size'],
    categories: [
      {
        key: 'coffees',
        preparationArea: 'bar',
        items: [
          { key: 'espresso', price: 2200 },
          { key: 'cortado', price: 2400 },
          { key: 'latte', price: 3200 },
          { key: 'cappuccino', price: 3200 },
          { key: 'tea', price: 2000 },
        ],
      },
      {
        key: 'bakery',
        preparationArea: 'kitchen',
        items: [
          { key: 'medialunas', price: 1500 },
          { key: 'tostado', price: 4500 },
          { key: 'croissant', price: 2600 },
          { key: 'budin', price: 2800 },
        ],
      },
      {
        key: 'pastries',
        preparationArea: 'kitchen',
        items: [
          { key: 'cheesecake', price: 4200 },
          { key: 'brownie', price: 3500 },
          { key: 'cookie', price: 1800 },
        ],
      },
      {
        key: 'juices',
        preparationArea: 'bar',
        items: [
          { key: 'orangeJuice', price: 2800 },
          { key: 'smoothie', price: 3600 },
          { key: 'water', price: 1200 },
        ],
      },
    ],
  },
  {
    id: 'parrilla',
    icon: 'Flame',
    modifierTemplateIds: ['doneness', 'sauce'],
    categories: [
      {
        key: 'starters',
        preparationArea: 'kitchen',
        items: [
          { key: 'provoleta', price: 6500 },
          { key: 'chorizo', price: 4200 },
          { key: 'morcilla', price: 4000 },
          { key: 'meatEmpanada', price: 1400 },
        ],
      },
      {
        key: 'meats',
        preparationArea: 'kitchen',
        items: [
          { key: 'asado', price: 16500 },
          { key: 'vacio', price: 15500 },
          { key: 'bife', price: 17500 },
          { key: 'entrana', price: 16000 },
          { key: 'grilledChicken', price: 11500 },
        ],
      },
      {
        key: 'sides',
        preparationArea: 'kitchen',
        items: [
          { key: 'fries', price: 3200 },
          { key: 'mixedSalad', price: 3800 },
          { key: 'pure', price: 3500 },
        ],
      },
      {
        key: 'drinks',
        preparationArea: 'bar',
        items: [{ key: 'wine', price: 6500 }, ...DRINKS_BASIC],
      },
      {
        key: 'desserts',
        preparationArea: 'kitchen',
        items: [
          { key: 'flan', price: 3200 },
          { key: 'panqueque', price: 3600 },
        ],
      },
    ],
  },
  {
    id: 'bar',
    icon: 'Beer',
    modifierTemplateIds: ['ice'],
    categories: [
      {
        key: 'beers',
        preparationArea: 'bar',
        items: [
          { key: 'ipa', price: 3800 },
          { key: 'lager', price: 3400 },
          { key: 'stout', price: 3900 },
          { key: 'honeyBeer', price: 3800 },
        ],
      },
      {
        key: 'cocktails',
        preparationArea: 'bar',
        items: [
          { key: 'ginTonic', price: 6200 },
          { key: 'fernet', price: 4800 },
          { key: 'aperol', price: 5800 },
          { key: 'mojito', price: 5800 },
        ],
      },
      {
        key: 'snacks',
        preparationArea: 'kitchen',
        items: [
          { key: 'nachos', price: 6500 },
          { key: 'bravas', price: 5200 },
          { key: 'wings', price: 7800 },
          { key: 'picada', price: 12500 },
        ],
      },
      {
        key: 'nonAlcoholic',
        preparationArea: 'bar',
        items: [
          { key: 'soda', price: 1800 },
          { key: 'water', price: 1200 },
          { key: 'lemonade', price: 2200 },
        ],
      },
    ],
  },
  {
    id: 'restaurant',
    icon: 'UtensilsCrossed',
    modifierTemplateIds: ['doneness', 'without'],
    categories: [
      {
        key: 'starters',
        preparationArea: 'kitchen',
        items: [
          { key: 'meatEmpanada', price: 1400 },
          { key: 'provoleta', price: 6500 },
          { key: 'bruschetta', price: 5200 },
          { key: 'soup', price: 4800 },
        ],
      },
      {
        key: 'mains',
        preparationArea: 'kitchen',
        items: [
          { key: 'milanesa', price: 11500 },
          { key: 'ravioles', price: 10500 },
          { key: 'salmon', price: 16500 },
          { key: 'risotto', price: 12000 },
          { key: 'bife', price: 17500 },
        ],
      },
      {
        key: 'sides',
        preparationArea: 'kitchen',
        items: [
          { key: 'fries', price: 3200 },
          { key: 'mixedSalad', price: 3800 },
          { key: 'pure', price: 3500 },
        ],
      },
      {
        key: 'desserts',
        preparationArea: 'kitchen',
        items: [
          { key: 'flan', price: 3200 },
          { key: 'tiramisu', price: 3800 },
          { key: 'iceCream', price: 3000 },
        ],
      },
      {
        key: 'drinks',
        preparationArea: 'bar',
        items: [...DRINKS_BASIC, { key: 'wine', price: 6500 }],
      },
    ],
  },
];

export function getStarterMenu(id) {
  return STARTER_MENUS.find((menu) => menu.id === id);
}

/** Cantidad total de items de un rubro (para el resumen de la card). */
export function countStarterItems(menu) {
  return (menu?.categories || []).reduce((acc, cat) => acc + cat.items.length, 0);
}
